import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { CheckCircle, ShieldCheck, AlertTriangle, Loader2, ChevronDown, FileText, ExternalLink, ArrowRight } from 'lucide-react';
import axios from 'axios';

import { getBackendUrl, buildPublicUrl } from '../services/origin';
const API = `${getBackendUrl()}/api`;

export default function ConsentPage() {
  const { token } = useParams();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [accepting, setAccepting] = useState(false);
  const [accepted, setAccepted] = useState(false);
  const [welcomePoints, setWelcomePoints] = useState(0);
  // Customer's personal dashboard URL — populated on successful accept so
  // we can redirect/let the user jump to their bacheca right away.
  const [dashboardUrl, setDashboardUrl] = useState('');
  const [redirectCountdown, setRedirectCountdown] = useState(5);
  const [hasScrolledToBottom, setHasScrolledToBottom] = useState(false);
  const [marketingConsent, setMarketingConsent] = useState(true);
  const [thirdPartyConsent, setThirdPartyConsent] = useState(false);
  const [profilingConsent, setProfilingConsent] = useState(true);
  const scrollRef = useRef(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const res = await axios.get(`${API}/public/consent/${token}`);
        if (res.data.already_accepted) {
          setAccepted(true);
        }
        setData(res.data);
      } catch (err) {
        const status = err.response?.status;
        if (status === 404) setError('Questo link non è valido o è già stato utilizzato.');
        else if (status === 410) setError('Questo link è scaduto. Chiedi allo staff di generarne uno nuovo.');
        else setError('Errore nel caricamento. Riprova più tardi.');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [token]);

  const checkScroll = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    const atBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 10;
    if (atBottom) setHasScrolledToBottom(true);
  }, []);

  useEffect(() => {
    const el = scrollRef.current;
    if (accepted) return;
    const privacyUrl = data?.privacy_policy_url || data?.privacy_policy_file_url;
    const sections = data?.privacy_policy_sections;
    // If we have inline sections (preferred) → keep gating behind real scroll.
    // If we only have an external NON-PDF link (legacy), auto-enable after a
    // brief delay since we can't observe scroll inside another tab.
    if (!sections?.length && privacyUrl && !privacyUrl.toLowerCase().includes('.pdf') && !privacyUrl.toLowerCase().includes('pdf')) {
      const timer = setTimeout(() => setHasScrolledToBottom(true), 1500);
      return () => clearTimeout(timer);
    }
    if (!el) return;
    // Check if content is short enough that no scroll is needed
    const timer = setTimeout(() => {
      if (el.scrollHeight <= el.clientHeight + 10) {
        setHasScrolledToBottom(true);
      }
    }, 500);
    return () => clearTimeout(timer);
  }, [data, accepted]);

  const scrollToBottom = () => {
    const el = scrollRef.current;
    if (el) el.scrollTo({ top: el.scrollHeight, behavior: 'smooth' });
  };

  // Auto-redirect to the customer's dashboard 5 seconds after a
  // successful accept. The countdown is visible so the user knows what's
  // about to happen (and can tap "Vai alla mia bacheca" earlier).
  useEffect(() => {
    if (!accepted || !dashboardUrl) return;
    if (redirectCountdown <= 0) {
      window.location.href = dashboardUrl;
      return;
    }
    const t = setTimeout(() => setRedirectCountdown(c => c - 1), 1000);
    return () => clearTimeout(t);
  }, [accepted, dashboardUrl, redirectCountdown]);

  const handleAccept = async () => {
    if (!profilingConsent) return;
    setAccepting(true);
    try {
      const res = await axios.post(`${API}/public/consent/${token}`, {
        marketing_consent: marketingConsent,
        third_party_consent: thirdPartyConsent,
      });
      setWelcomePoints(res.data.welcome_points || 0);
      // Build the customer's personal dashboard URL on the SAME origin
      // the customer just used (host-agnostic). Pre-fills the PIN so
      // they don't have to type it from the SMS.
      if (res.data.dashboard_token) {
        const pinPart = res.data.dashboard_pin ? `?pin=${encodeURIComponent(res.data.dashboard_pin)}` : '';
        setDashboardUrl(buildPublicUrl(`/my/${res.data.dashboard_token}${pinPart}`));
      }
      setAccepted(true);
    } catch (err) {
      const status = err.response?.status;
      if (status === 410) setError('Questo link è scaduto. Chiedi allo staff di generarne uno nuovo.');
      else setError('Errore durante la conferma. Riprova.');
    } finally {
      setAccepting(false);
    }
  };

  const primaryColor = data?.branding?.primary_color || '#D44E20';
  const bgColor = data?.branding?.background_color || '#111114';
  const secondaryColor = data?.branding?.secondary_color || '#3ECF8E';
  const businessName = data?.business_name || 'il locale';

  if (loading) {
    return (
      <div className="min-h-dvh flex items-center justify-center" style={{ background: bgColor }}>
        <Loader2 className="animate-spin text-white" size={32} />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-dvh flex items-center justify-center p-6" style={{ background: bgColor }}>
        <div className="text-center max-w-sm">
          <div className="w-16 h-16 bg-[#FF5555]/20 rounded-full flex items-center justify-center mx-auto mb-4">
            <AlertTriangle size={32} className="text-[#FF5555]" />
          </div>
          <h2 className="text-white font-bold text-xl mb-2">Ops!</h2>
          <p className="text-[#888] text-base" data-testid="consent-error-msg">{error}</p>
        </div>
      </div>
    );
  }

  if (accepted) {
    return (
      <div className="min-h-dvh flex items-center justify-center p-6" style={{ background: bgColor }}>
        <div className="text-center max-w-sm" data-testid="consent-success">
          <div className="w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-5" style={{ background: `${secondaryColor}20`, border: `2px solid ${secondaryColor}` }}>
            <CheckCircle size={40} style={{ color: secondaryColor }} />
          </div>
          <h2 className="text-white font-bold text-2xl mb-2">Iscrizione confermata!</h2>
          <p className="text-[#888] text-base mb-4">
            Benvenuto nel programma fedeltà di <span className="text-white font-semibold">{businessName}</span>!
          </p>
          {welcomePoints > 0 && (
            <div className="rounded-2xl p-5 border inline-block mb-4" style={{ background: '#18181E', borderColor: `${secondaryColor}40` }}>
              <div className="text-4xl font-bold tabular-nums" style={{ color: secondaryColor }}>+{welcomePoints}</div>
              <div className="text-[#888] text-sm mt-1">punti benvenuto</div>
            </div>
          )}
          {dashboardUrl ? (
            <div className="mt-2">
              <a
                href={dashboardUrl}
                data-testid="goto-dashboard-btn"
                className="w-full inline-flex items-center justify-center gap-2 h-12 rounded-2xl font-semibold text-base text-white transition-transform active:scale-[0.98]"
                style={{ background: primaryColor }}
              >
                Vai alla mia bacheca
                <ArrowRight size={18} />
              </a>
              <p className="text-[#555] text-xs mt-3" data-testid="redirect-countdown">
                Reindirizzamento automatico tra <span className="font-semibold text-[#888]">{redirectCountdown}s</span>…
              </p>
            </div>
          ) : (
            <p className="text-[#555] text-sm mt-4">Puoi chiudere questa pagina.</p>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-dvh flex flex-col" style={{ background: bgColor }} data-testid="consent-page">
      <div className="flex-1 flex flex-col items-center px-5 pt-8 pb-6 max-w-lg mx-auto w-full">
        <div className="w-14 h-14 rounded-full flex items-center justify-center mb-4" style={{ background: `${primaryColor}20`, border: `2px solid ${primaryColor}` }}>
          <ShieldCheck size={28} style={{ color: primaryColor }} />
        </div>

        <h1 className="text-white font-bold text-2xl text-center mb-1" data-testid="consent-title">
          {businessName}
        </h1>
        <p className="text-[#888] text-sm text-center mb-5">
          Ciao <span className="text-white font-medium">{data?.customer_name}</span>, per completare l'iscrizione leggi e accetta l'informativa sulla privacy.
        </p>

        {/* Privacy Policy - inline sections (preferred) → external URL fallback */}
        <PrivacyPolicySection
          sections={data?.privacy_policy_sections}
          privacyUrl={data?.privacy_policy_url || data?.privacy_policy_file_url}
          businessName={businessName}
          primaryColor={primaryColor}
          scrollRef={scrollRef}
          checkScroll={checkScroll}
          hasScrolledToBottom={hasScrolledToBottom}
          scrollToBottom={scrollToBottom}
        />

        {/* Consent checkboxes */}
        {hasScrolledToBottom && (
          <div className="w-full space-y-3 mb-4" data-testid="consent-checkboxes">
            {/* 1. Profiling - mandatory */}
            <label className="flex items-start gap-3 p-3 rounded-xl border cursor-pointer transition-all"
              style={{ background: profilingConsent ? `${primaryColor}10` : '#18181E', borderColor: profilingConsent ? primaryColor : 'rgba(255,255,255,0.1)' }}>
              <input type="checkbox" checked={profilingConsent} onChange={e => setProfilingConsent(e.target.checked)}
                data-testid="consent-profiling-checkbox"
                className="mt-0.5 w-4 h-4 accent-[#D44E20] flex-shrink-0" />
              <div>
                <span className="text-white text-xs font-medium">Programma fedelta e profilazione</span>
                <span className="text-[#FF5555] text-xs ml-1">*</span>
                <p className="text-[#888] text-[10px] mt-0.5">Acconsento al trattamento dei dati per la partecipazione al programma, inclusa profilazione e analisi comportamentale.</p>
              </div>
            </label>

            {/* 2. Marketing - strongly recommended */}
            <label className="flex items-start gap-3 p-3 rounded-xl border cursor-pointer transition-all"
              style={{ background: marketingConsent ? `${primaryColor}10` : '#18181E', borderColor: marketingConsent ? primaryColor : 'rgba(255,255,255,0.1)' }}>
              <input type="checkbox" checked={marketingConsent} onChange={e => setMarketingConsent(e.target.checked)}
                data-testid="consent-marketing-checkbox"
                className="mt-0.5 w-4 h-4 accent-[#D44E20] flex-shrink-0" />
              <div>
                <span className="text-white text-xs font-medium">Comunicazioni di marketing</span>
                <p className="text-[#888] text-[10px] mt-0.5">Acconsento a ricevere comunicazioni promozionali tramite WhatsApp, email e SMS.</p>
                {!marketingConsent && (
                  <div className="mt-1.5 p-2 bg-[#FF555520] rounded-lg border border-[#FF555540]">
                    <p className="text-[#FF8888] text-[10px] leading-relaxed">
                      Senza questo consenso non potrai ricevere notifiche su punti guadagnati, premi sbloccati, promozioni esclusive e tutti i vantaggi del programma. Ti consigliamo fortemente di accettare.
                    </p>
                  </div>
                )}
              </div>
            </label>

            {/* 3. Third party - optional */}
            <label className="flex items-start gap-3 p-3 rounded-xl border cursor-pointer transition-all"
              style={{ background: thirdPartyConsent ? `${primaryColor}10` : '#18181E', borderColor: thirdPartyConsent ? primaryColor : 'rgba(255,255,255,0.1)' }}>
              <input type="checkbox" checked={thirdPartyConsent} onChange={e => setThirdPartyConsent(e.target.checked)}
                data-testid="consent-thirdparty-checkbox"
                className="mt-0.5 w-4 h-4 accent-[#D44E20] flex-shrink-0" />
              <div>
                <span className="text-white text-xs font-medium">Condivisione con piattaforme terze</span>
                <span className="text-[#888] text-[10px] ml-1">(opzionale)</span>
                <p className="text-[#888] text-[10px] mt-0.5">Acconsento alla condivisione dei miei dati con Meta, Google e altre piattaforme per pubblicita personalizzata.</p>
              </div>
            </label>
          </div>
        )}

        {/* Accept button */}
        <button
          onClick={handleAccept}
          disabled={accepting || !hasScrolledToBottom || !profilingConsent}
          data-testid="consent-accept-btn"
          className="w-full h-14 text-white font-bold text-lg rounded-[14px] disabled:opacity-30 active:scale-95 transition-all flex items-center justify-center gap-2 shadow-lg"
          style={{
            background: (hasScrolledToBottom && profilingConsent) ? primaryColor : '#333',
            boxShadow: (hasScrolledToBottom && profilingConsent) ? `0 10px 25px -5px ${primaryColor}40` : 'none',
          }}
        >
          {accepting ? (
            <Loader2 size={20} className="animate-spin" />
          ) : (
            <ShieldCheck size={20} />
          )}
          {accepting ? 'Conferma in corso...' : 'Accetto e mi iscrivo'}
        </button>

        <p className="text-[#555] text-xs text-center mt-3">
          I campi contrassegnati con * sono obbligatori per l'iscrizione al programma.
        </p>
      </div>
    </div>
  );
}


/* ── PRIVACY POLICY SECTION ──────────────────────────────── */
function PrivacyPolicySection({ sections, privacyUrl, businessName, primaryColor, scrollRef, checkScroll, hasScrolledToBottom, scrollToBottom }) {
  const [pdfLoaded, setPdfLoaded] = useState(false);

  // PREFERRED: AI-generated sections are rendered inline so we can really
  // gate the consent buttons on a scroll-to-bottom event. Falls back to
  // an external URL only when no sections were generated yet.
  const hasSections = Array.isArray(sections) && sections.length > 0;

  if (hasSections) {
    return (
      <>
        <div className="w-full relative mb-4 flex-1" style={{ minHeight: '40vh', maxHeight: '55vh' }}>
          <div
            className="w-full h-full bg-[#18181E] border border-white/10 rounded-2xl overflow-hidden flex flex-col"
            style={{ minHeight: '40vh', maxHeight: '55vh' }}
            data-testid="consent-privacy-box"
          >
            {/* Header */}
            <div className="px-5 pt-4 pb-3 border-b border-white/10 flex items-center gap-3">
              <FileText size={18} style={{ color: primaryColor }} />
              <div>
                <p className="text-white text-sm font-semibold">Informativa sulla Privacy</p>
                <p className="text-[#888] text-[10px]">{businessName} — Ai sensi del GDPR (Reg. UE 2016/679)</p>
              </div>
            </div>

            {/* Inline scrollable content — `scrollRef` lives on this element
                so the parent's `checkScroll` listener observes the right
                element (the one that actually scrolls) */}
            <div
              ref={scrollRef}
              onScroll={checkScroll}
              className="flex-1 overflow-y-auto px-5 py-4 text-[#cfcfcf] text-[12px] leading-relaxed"
              data-testid="privacy-inline-text"
            >
              {sections.map((s, i) => (
                <div key={i} className="mb-4">
                  <h3 className="text-white text-[13px] font-semibold mb-1.5">{s.title}</h3>
                  {(s.paragraphs || []).map((p, pi) => (
                    p.startsWith('- ') ? (
                      <div key={pi} className="ml-3 mb-1 flex gap-2">
                        <span className="text-[#888]">•</span>
                        <span>{p.slice(2)}</span>
                      </div>
                    ) : (
                      <p key={pi} className="mb-1.5">{p}</p>
                    )
                  ))}
                </div>
              ))}
              {/* Bottom anchor — when this becomes visible the scroll-to-end
                  fires (handled by the IntersectionObserver below) */}
              <div data-testid="privacy-end-anchor" style={{ height: 1 }} />
            </div>
          </div>

          {/* Scroll-to-bottom arrow (always visible until reached) */}
          {!hasScrolledToBottom && (
            <button
              onClick={scrollToBottom}
              data-testid="scroll-to-bottom-btn"
              className="absolute bottom-3 right-3 w-10 h-10 rounded-full flex items-center justify-center shadow-lg transition-all active:scale-90 animate-bounce"
              style={{ background: primaryColor }}
              aria-label="Vai in fondo"
            >
              <ChevronDown size={22} className="text-white" />
            </button>
          )}
        </div>

        {!hasScrolledToBottom && (
          <p className="text-[#666] text-xs text-center mb-3" data-testid="scroll-hint">
            Scorri fino in fondo per poter accettare
          </p>
        )}

        {/* Stable, deploy-agnostic shareable link to the same policy.
            Useful for users who want to bookmark / re-read it later
            without reopening the consent flow (which is one-time). */}
        <p className="text-[#666] text-[10px] text-center mb-2">
          <a
            href="/privacy-policy"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="consent-privacy-standalone-link"
            className="underline hover:text-[#999] inline-flex items-center gap-1"
          >
            <ExternalLink size={10} /> Apri l'informativa in una pagina dedicata
          </a>
        </p>
      </>
    );
  }

  // FALLBACK: external URL (legacy)
  if (privacyUrl) {
    const isPdf = privacyUrl.toLowerCase().includes('.pdf') || privacyUrl.toLowerCase().includes('pdf');

    return (
      <>
        <div className="w-full relative mb-4 flex-1" style={{ minHeight: '40vh', maxHeight: '55vh' }}>
          <div
            ref={scrollRef}
            onScroll={checkScroll}
            className="w-full h-full bg-[#18181E] border border-white/10 rounded-2xl overflow-hidden flex flex-col"
            style={{ minHeight: '40vh', maxHeight: '55vh' }}
            data-testid="consent-privacy-box"
          >
            {/* Header */}
            <div className="px-5 pt-4 pb-3 border-b border-white/10 flex items-center gap-3">
              <FileText size={18} style={{ color: primaryColor }} />
              <div>
                <p className="text-white text-sm font-semibold">Informativa sulla Privacy</p>
                <p className="text-[#888] text-[10px]">{businessName} — Ai sensi del GDPR</p>
              </div>
            </div>

            {/* PDF iframe or link fallback */}
            {isPdf ? (
              <div className="flex-1 relative">
                <iframe
                  src={`${privacyUrl}#toolbar=0`}
                  title="Privacy Policy"
                  className="w-full h-full border-0"
                  style={{ minHeight: '30vh' }}
                  onLoad={() => {
                    setPdfLoaded(true);
                    // Auto-enable accept since PDF is loaded
                    if (scrollRef.current) {
                      const el = scrollRef.current;
                      if (el.scrollHeight <= el.clientHeight + 10) {
                        // Content fits, no scroll needed
                      }
                    }
                  }}
                  data-testid="privacy-pdf-iframe"
                />
                {!pdfLoaded && (
                  <div className="absolute inset-0 flex items-center justify-center">
                    <Loader2 className="animate-spin text-white" size={24} />
                  </div>
                )}
              </div>
            ) : (
              <div className="flex-1 flex items-center justify-center p-6">
                <a href={privacyUrl} target="_blank" rel="noopener noreferrer"
                  className="flex items-center gap-3 px-6 py-4 rounded-xl border border-white/10 bg-white/5 transition-all active:scale-95"
                  data-testid="privacy-external-link"
                >
                  <ExternalLink size={20} style={{ color: primaryColor }} />
                  <div>
                    <p className="text-white text-sm font-medium">Leggi l'informativa privacy</p>
                    <p className="text-[#888] text-[10px]">Si aprirà in una nuova scheda</p>
                  </div>
                </a>
              </div>
            )}
          </div>

          {/* Scroll-to-bottom arrow */}
          {!hasScrolledToBottom && isPdf && (
            <button
              onClick={scrollToBottom}
              data-testid="scroll-to-bottom-btn"
              className="absolute bottom-3 right-3 w-10 h-10 rounded-full flex items-center justify-center shadow-lg transition-all active:scale-90 animate-bounce"
              style={{ background: primaryColor }}
            >
              <ChevronDown size={22} className="text-white" />
            </button>
          )}
        </div>

        {/* For external links, auto-enable scrolled state after brief delay */}
        {!isPdf && !hasScrolledToBottom && <AutoScrollEnabler />}

        {/* Hint */}
        {!hasScrolledToBottom && isPdf && (
          <p className="text-[#666] text-xs text-center mb-3" data-testid="scroll-hint">
            Scorri fino in fondo per poter accettare
          </p>
        )}
      </>
    );
  }

  // Fallback: generic privacy text (no custom policy uploaded)
  return (
    <>
      <div className="w-full relative mb-4 flex-1" style={{ minHeight: '40vh', maxHeight: '55vh' }}>
        <div
          ref={scrollRef}
          onScroll={checkScroll}
          className="w-full h-full bg-[#18181E] border border-white/10 rounded-2xl p-5 overflow-y-auto"
          style={{ minHeight: '40vh', maxHeight: '55vh' }}
          data-testid="consent-privacy-box"
        >
          <FallbackPrivacyText businessName={businessName} primaryColor={primaryColor} />
        </div>

        {!hasScrolledToBottom && (
          <button
            onClick={scrollToBottom}
            data-testid="scroll-to-bottom-btn"
            className="absolute bottom-3 right-3 w-10 h-10 rounded-full flex items-center justify-center shadow-lg transition-all active:scale-90 animate-bounce"
            style={{ background: primaryColor }}
          >
            <ChevronDown size={22} className="text-white" />
          </button>
        )}
      </div>

      {!hasScrolledToBottom && (
        <p className="text-[#666] text-xs text-center mb-3" data-testid="scroll-hint">
          Scorri fino in fondo per poter accettare
        </p>
      )}
    </>
  );
}

function AutoScrollEnabler() {
  // For external link policies, auto-enable accept after the user has had time to read the link
  useEffect(() => {
    // We can't track scroll on external link, so auto-enable after 2 seconds
    const timer = setTimeout(() => {
      // Trigger a custom event or use a ref - but simplest: just show checkboxes
      // The parent uses hasScrolledToBottom which we can't set from here
      // Instead, this component is rendered when !hasScrolledToBottom && !isPdf
      // The parent's useEffect with timeout should handle this
    }, 2000);
    return () => clearTimeout(timer);
  }, []);
  return null;
}

function FallbackPrivacyText({ businessName, primaryColor }) {
  return (
    <div className="text-[#999] text-sm leading-relaxed space-y-3">
      <p className="font-semibold text-white text-base">Informativa sulla Privacy e Trattamento dei Dati Personali</p>
      <p className="text-xs" style={{ color: primaryColor }}>Ai sensi del Regolamento UE 2016/679 (GDPR)</p>

      <p><strong className="text-white">1. Titolare del trattamento</strong></p>
      <p>Il Titolare del trattamento dei dati personali è <strong className="text-white">{businessName}</strong> (di seguito "il Titolare"), raggiungibile presso il locale stesso.</p>

      <p><strong className="text-white">2. Dati personali raccolti</strong></p>
      <p>Nell'ambito dell'adesione al programma fedeltà, il Titolare raccoglie e tratta i seguenti dati personali:</p>
      <ul className="list-disc list-inside space-y-1 pl-2">
        <li>Nome e cognome</li>
        <li>Numero di telefono</li>
        <li>Indirizzo email (se fornito)</li>
        <li>Data di nascita (se fornita)</li>
        <li>Dati relativi alle transazioni e ai punti accumulati</li>
      </ul>

      <p><strong className="text-white">3. Finalità del trattamento</strong></p>
      <p>I dati personali saranno trattati per le seguenti finalità:</p>
      <ul className="list-disc list-inside space-y-1 pl-2">
        <li>Gestione dell'iscrizione e partecipazione al programma fedeltà</li>
        <li>Accumulo, gestione e riscatto dei punti fedeltà</li>
        <li>Gestione dei premi e dei benefici legati al livello (tier) del cliente</li>
        <li>Invio di comunicazioni relative al programma tramite WhatsApp, SMS o email</li>
        <li>Invio di promozioni, offerte personalizzate e comunicazioni di marketing</li>
        <li>Analisi statistiche e miglioramento del servizio</li>
      </ul>

      <p><strong className="text-white">4. Base giuridica del trattamento</strong></p>
      <p>Il trattamento dei dati si basa sul consenso esplicito dell'interessato (art. 6, comma 1, lett. a del GDPR), che viene prestato mediante l'accettazione della presente informativa.</p>

      <p><strong className="text-white">5. Modalità del trattamento</strong></p>
      <p>I dati sono trattati con strumenti elettronici e conservati su server sicuri. Sono adottate misure di sicurezza tecniche e organizzative adeguate per proteggere i dati da accessi non autorizzati, perdita o distruzione.</p>

      <p><strong className="text-white">6. Conservazione dei dati</strong></p>
      <p>I dati personali saranno conservati per tutta la durata della partecipazione al programma fedeltà e per un periodo successivo di 24 mesi dalla cancellazione dell'iscrizione, salvo obblighi di legge che richiedano una conservazione più lunga.</p>

      <p><strong className="text-white">7. Comunicazione e diffusione dei dati</strong></p>
      <p>I dati personali non saranno ceduti, venduti o diffusi a terzi. Potranno essere comunicati a fornitori di servizi tecnici (es. piattaforme di messaggistica, hosting) esclusivamente per le finalità sopra indicate e nel rispetto del GDPR.</p>

      <p><strong className="text-white">8. Diritti dell'interessato</strong></p>
      <p>In qualsiasi momento, l'interessato può esercitare i seguenti diritti previsti dagli articoli 15-22 del GDPR:</p>
      <ul className="list-disc list-inside space-y-1 pl-2">
        <li><strong className="text-[#bbb]">Diritto di accesso:</strong> ottenere conferma del trattamento e copia dei dati</li>
        <li><strong className="text-[#bbb]">Diritto di rettifica:</strong> correggere dati inesatti o incompleti</li>
        <li><strong className="text-[#bbb]">Diritto alla cancellazione:</strong> richiedere la cancellazione dei dati</li>
        <li><strong className="text-[#bbb]">Diritto alla limitazione:</strong> limitare il trattamento in determinati casi</li>
        <li><strong className="text-[#bbb]">Diritto alla portabilità:</strong> ricevere i dati in formato strutturato</li>
        <li><strong className="text-[#bbb]">Diritto di opposizione:</strong> opporsi al trattamento per finalità di marketing</li>
        <li><strong className="text-[#bbb]">Diritto di revoca del consenso:</strong> revocare il consenso in qualsiasi momento</li>
      </ul>
      <p>Per esercitare i propri diritti, è sufficiente contattare il locale direttamente o inviare una comunicazione al Titolare.</p>

      <p><strong className="text-white">9. Reclamo</strong></p>
      <p>L'interessato ha il diritto di proporre reclamo all'Autorità Garante per la protezione dei dati personali (www.garanteprivacy.it) qualora ritenga che il trattamento violi il GDPR.</p>

      <p><strong className="text-white">10. Aggiornamenti</strong></p>
      <p>La presente informativa può essere aggiornata periodicamente. Eventuali modifiche saranno comunicate tramite i canali del programma fedeltà.</p>

      <div className="pt-3 border-t border-white/10 mt-3">
        <p className="text-[#666] text-xs">Ultimo aggiornamento: Marzo 2026</p>
      </div>
    </div>
  );
}
